import { Controller } from "@hotwired/stimulus";

import { submitLine } from "../../shared/manual_order_line_quantities";

/**
 * WHAT ONE MODIFIER GROUP MAY HOLD (CAL-509).
 *
 * `CreateForm#validate_group` counts
 * `selected_ids.size + quantity_count_for(group)`, so a
 * checked pill and a quantity option above zero each
 * occupy ONE slot, and a group of `max: 1` holding both
 * is refused. The UI happily built that state and then
 * blamed the merchant for it: on venue 1638 "Jalapeños"
 * is `max: 1` over a `Si` that steps to 4 and a `No`
 * that is a checkbox, and the merchant had to TYPE a
 * zero into `Si` by hand before they were allowed to
 * click `No`.
 *
 * 15 production groups are in that shape and 14 of them
 * are `single, required, min: 1, max: 1`: a RADIO beside
 * a stepper. Radios exclude each other natively and know
 * nothing about the stepper next to them, so the trap is
 * if anything likelier there, and with `required` there
 * is no None pill to climb back out through. Clearing
 * the radio when a stepper is raised is the ONLY way out
 * of those groups.
 *
 * SO A GROUP OF ONE IS EXCLUSIVE: the press that arrives
 * wins and everything else in the group is cleared, in
 * both directions.
 *
 * A GROUP OF MORE THAN ONE FILLS UP INSTEAD. There is no
 * honest answer to "which of the two you already picked
 * did you mean to drop", and dropping the oldest is not
 * even answerable: the server renders no order over a
 * group's selections, so "oldest" would be whatever the
 * page happened to draw first. Refusing the press is the
 * other option and a silent refusal is worse than the
 * server's, so nothing is refused: the options that
 * cannot fit are DISABLED while the group is full, and
 * come back the moment something is freed.
 *
 * THE SUBMIT IS HERE, not on the input, because the
 * order matters. A `change` action on the pill itself
 * fires before this one (the event has to bubble), so
 * the request would carry the very state this exists to
 * prevent.
 */
export default class extends Controller {
  static targets = ["option", "quantity", "quantityField"];

  static values = {
    max: Number,
    lineId: String,
    formId: String,
  };

  connect() {
    this.publishLimit();
  }

  optionChanged(event) {
    if (event.target.checked) this.clearOthers(event.target);

    this.publishLimit();
    submitLine(this.formIdValue, this.lineIdValue);
  }

  /**
   * A stepper moved. It schedules its own (debounced)
   * submit, so nothing is posted from here: what this
   * owes is only the state that has to be right BEFORE
   * that request goes out.
   */
  quantityChanged(event) {
    if (this.exclusive && event.detail.value > 0) {
      this.clearOptions();
      this.clearQuantities(event.target);
    }

    this.publishLimit();
  }

  clearOthers(pressed) {
    if (!this.exclusive) return;

    this.optionTargets.forEach((option) => {
      if (option !== pressed) option.checked = false;
    });

    this.clearQuantities();
  }

  /**
   * Back to no pill at all, or to the None pill where
   * the group offers one. A `required` group has none,
   * and leaving every radio unchecked is exactly right
   * there: the stepper the merchant just raised is the
   * group's answer.
   */
  clearOptions() {
    let none = null;

    this.optionTargets.forEach((option) => {
      if (option.value === "") {
        none = option;
        return;
      }

      option.checked = false;
    });

    if (none) none.checked = true;
  }

  clearQuantities(except = null) {
    this.quantityTargets.forEach((element) => {
      if (element === except) return;

      this.dispatch("clear", { target: element });
    });
  }

  /**
   * Only above one. A group of one never disables
   * anything: it swaps, so every option stays pressable
   * and the merchant is never stuck.
   */
  publishLimit() {
    if (!this.maxValue || this.exclusive) return;

    const full = this.occupancy >= this.maxValue;

    this.quantityTargets.forEach((element) => {
      this.dispatch("limit", {
        target: element,
        detail: { full },
      });
    });

    this.optionTargets.forEach((option) => {
      // Never the None pill: it takes no slot, and
      // disabling it would close the way out.
      if (option.value === "") return;

      option.disabled = full && !option.checked;
    });
  }

  get occupancy() {
    const chosen = this.optionTargets.filter(
      (option) => option.checked && option.value !== ""
    ).length;

    const counted = this.quantityFieldTargets.filter(
      (field) => Number(field.value.replace(/[^0-9]/g, "") || 0) > 0
    ).length;

    return chosen + counted;
  }

  get exclusive() {
    return this.maxValue === 1;
  }
}
